import { HttpService } from '@ribajs/core'
import pRetry, { AbortError } from 'p-retry'
import type {
  ShopifyProductRecommendations,
  ShopifyProduct,
} from '../types/index.js'

export interface RecommendationsCache {
  [handle: string]: ShopifyProduct[]
}

/**
 * Get a product recommendations
 */
export class ShopifyProductRecommendationsService {
  /**
   * Do nothing at the moment
   * @param product
   */
  private static prepareProduct(product: ShopifyProduct) {
    return product
  }

  private static prepareProducts(products: ShopifyProduct[]) {
    return products.map(this.prepareProduct)
  }

  private static async _getSingle(
    productId: number,
    limit = 4,
  ): Promise<ShopifyProduct[]> {
    const locale = (window as any)?.Shopify?.routes?.root || '/'
    const cacheKey = `${productId}-${limit}`
    if (this.cache[cacheKey]) {
      return this.cache[cacheKey]
    } else {
      const res = await HttpService.getJSON<ShopifyProductRecommendations>(
        `${locale}recommendations/products.json?product_id=${productId}`,
      )
      if (res.status === 404) {
        throw new AbortError(res.statusText)
      }
      const products = this.prepareProducts(res.body?.products)

      this.cache[cacheKey] = products
      return this.cache[cacheKey]
    }
  }

  /**
   * Get a single product recommendations by productId
   * @param productId product id
   */
  public static async getSingle(
    productId: number,
    limit = 4,
  ): Promise<ShopifyProduct[]> {
    return pRetry(this._getSingle.bind(this, productId, limit))
  }

  /**
   * Get product recommendations for multiple products by productId's
   * @param productIds product id's
   */
  public static async get(
    productIds: number[] | number,
    limit = 4,
  ): Promise<ShopifyProduct[]> {
    if (typeof productIds === 'number') {
      productIds = [productIds]
    }
    const count = productIds.length
    const limitPerProduct = Math.round(limit / count + 0.5)
    let result: ShopifyProduct[] = []
    for (const productId of productIds) {
      result.push(...(await this.getSingle(productId, limitPerProduct)))
    }
    result = result.slice(0, limit)
    return result
  }

  protected static cache: RecommendationsCache = {}
}
